import React from "react";
import IconTooltip, { IconTooltipType } from "src/components/iconTooltip";
import "./field.scss";

interface IFieldProps {
  className?: string;
  label: React.ReactNode;
  value?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
  maxLength?: number;
  /**
   * A line rendered under the input, for a constraint the user needs while
   * typing or for the error itself as readable text.
   *
   * Passing it also suppresses the error tooltip: the tooltip is the only
   * place an error appears otherwise, and it needs a hover the caller cannot
   * rely on — so a caller that takes over the message must not end up showing
   * it twice.
   *
   * An empty string is meaningful and is not the same as omitting the prop: it
   * keeps the line's box without printing anything, so a field whose hint
   * exists for some payout methods and not others keeps its height as the user
   * switches between them.
   */
  assist?: React.ReactNode;
  /**
   * Virtual-keyboard hint, e.g. `"decimal"` for money. Left unset the field
   * stays a plain text input, which is what every existing caller gets.
   */
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  /**
   * A control to sit at the end of the label line — a `Max` shortcut, a unit
   * toggle. It is a sibling of the `<label>`, never a child, and that is the
   * whole point: a browser applies `:hover` to a control whenever the pointer
   * is over its label, so a label wrapping the full row lit the input's hover
   * border from anywhere along it, including the empty gap.
   */
  labelAside?: React.ReactNode;
}

const Field: React.FC<IFieldProps> = ({
  className,
  label,
  value = "",
  onChange,
  placeholder,
  error,
  disabled = false,
  onBlur,
  maxLength,
  assist,
  inputMode,
  labelAside,
}) => {
  // Unique per instance: the tooltip id used to be derived from
  // `className`, but most Fields render without one — they all collided
  // on `field-tooltip-undefined`, so a single error icon fired every
  // mounted Field's tooltip at once (duplicate popups).
  const uid = React.useId();
  const tooltipId = `field-tooltip-${uid}`;
  const inputId = `field-input-${uid}`;
  const assistId = `field-assist-${uid}`;

  const fieldClass = `field ${disabled ? "field-disabled" : ""} ${className || ""} ${error ? "field-error" : ""}`;

  return (
    <div className={fieldClass}>
      {labelAside ? (
        <div className="field-label-row">
          <label className="field-label" htmlFor={inputId}>
            {label}
          </label>
          {labelAside}
        </div>
      ) : (
        <label className="field-label" htmlFor={inputId}>
          {label}
        </label>
      )}

      <input
        id={inputId}
        type="text"
        inputMode={inputMode}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="field-input"
        onBlur={onBlur}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={assist ? assistId : undefined}
      />

      {error && !assist && (
        <IconTooltip
          iconType={IconTooltipType.INFO}
          className="icon-tooltip-negative field-icon-tooltip"
          tooltipId={tooltipId}
          tooltipContent={error}
        />
      )}

      {assist !== undefined && (
        <p id={assistId} className="field-assist">
          {assist}
        </p>
      )}
    </div>
  );
};

export default Field;
